import React, {
  useMemo,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spacer } from '@tw/tw-component-library';
import { Heading } from '@tw/tw-component-library/common-wt';
import {
  workflowsRegistry,
  createWorkflowWithAuth,
} from '../../../utils/workflowRegistry';
import { Container } from '../../pages/workflows/styles';
import { UseAuth } from '../../../contexts/AuthContext';
import {
  canAccessWorkflow,
  canViewWorkflow,
  getSpoofUserHeader,
  identityFromSpoofedUser,
} from '../../../utils/permissions';
import { getWorkFlow } from '../../../apis/workflow/get/getWorkFlow';
import { involvementPermissions } from '../../../apis/permissions/get/involvementPermissions';
import { getAssignedWorkflows } from '../../../apis/workflow/get/getAssignedWorkflows';
import { mapWorkflowToFormData } from '../../pages/RequestDetails/mappers/workflowMapper';
import { isGiftHospitalityWorkflow } from '../../container/forms/schemas/GiftHospitalityDonation/authoriserRules';
import { hasWorkflowCreator } from '../../container/forms/schemas/GiftHospitalityDonation/givenReceivedByPerson';
import { useFormContext } from '../../../contexts/useFormContext';
import { getWorkflowTypeFromDisplayName } from '../../../components/container/forms/types/api/graph.types';
import { PaymentWorkflowType } from '../../../components/container/forms/types/api/graph.types';
import { useFormConfiguration } from '../../../components/container/forms/hooks/core/useFormConfiguration';
import { FormStackComponent } from '../../pages/RequestDetails/FormStackComponent';
import { FormSection } from '../../container/forms/components/FormSection';
import {
  FormWrapper,
  FormWrapperRef,
} from '../../pages/RequestDetails/FormWrapper';
import { FormStackProps, FormDataType } from '@tw/tw-component-library';
import { useSchemas } from '../../../components/container/forms/hooks/schemas/useSchemas';
import { useWorkflowManager } from './WorkflowManager';
import { createLogger } from '../../../utils/logger';
import {
  buildDummyOvertimeFormState,
  isDummyOvertimeWorkflowId,
} from '../../container/forms/schemas/OvertimeApproval/dummyWorkflow';
import { PermissionOverwriteBanner } from '../PermissionOverwriteBanner';
import {
  buildTestingDummyFormState,
  getTestingDummyWorkflowId,
  isTestingDummyWorkflowId,
  shouldBypassWorkflowBackend,
  shouldBypassWorkflowBackendByKey,
} from '../../../utils/workflowTestingBypass';
import { stripSchemaValidationForTesting } from '../../container/forms/utils/stripSchemaValidationForTesting';
import type { FormSchemaConfig } from '../../container/forms/types';

// Constants
const DEFAULT_SECTION_KEY = 'payment_details';
const CREATOR_LOAD_ATTEMPTS = 5;
const CREATOR_LOAD_DELAY_MS = 400;

const wait = (delayMs: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });

// Helper functions
const extractSectionKeys = (data: {
  sectionProgress?: Record<string, unknown>;
}): string[] => {
  return Object.keys(data.sectionProgress || {});
};

const createSingleSectionFormConfig = (
  formSchema: FormStackProps['formSchema'] | null,
  formConfiguration: ReturnType<typeof useFormConfiguration>,
  bypassValidation: boolean,
): FormStackProps => {
  if (!formSchema) {
    return {
      formSchema: { sections: [] },
      initialData: {},
      validateOnChange: !bypassValidation,
      validateOnBlur: !bypassValidation,
    };
  }

  const schemaSource = bypassValidation
    ? (stripSchemaValidationForTesting(
        formSchema as unknown as FormSchemaConfig,
      ) ?? formSchema)
    : formSchema;

  // Process fields recursively (handles nested grouped sections e.g. Approvers)
  const processedFormSchema = {
    ...schemaSource,
    sections: schemaSource.sections?.map((section) =>
      formConfiguration.processEnhancedSection(
        section as Parameters<
          typeof formConfiguration.processEnhancedSection
        >[0],
      ),
    ),
  };

  return {
    formSchema: processedFormSchema as unknown as FormStackProps['formSchema'],
    initialData: formConfiguration.formManager?.formState.data || {},
    validateOnChange: !bypassValidation,
    validateOnBlur: !bypassValidation,
    additionalProps: {
      ...formConfiguration.formConfig?.additionalProps,
      updateFormData: formConfiguration.formManager?.updateFormData,
    } as FormStackProps['additionalProps'] & {
      updateFormData?: (data: Record<string, unknown>) => void;
    },
  };
};

// ============= Logger =============
const logger = createLogger('CentralizedFormPage');

export const CentralizedFormPage: React.FC = () => {
  const { workflowKey, id } = useParams<{ workflowKey: string; id?: string }>();
  const navigate = useNavigate();
  const { getAccessToken, spoofedUser, currentUser, originalUser } = UseAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creatorFetchSettled, setCreatorFetchSettled] = useState(false);
  const formWrapperRef = useRef<FormWrapperRef>(null);

  const { formState, updateFormData, resetForm } = useFormContext();
  const formConfiguration = useFormConfiguration();
  const schemas = useSchemas();

  // Use the enhanced workflow manager
  const workflowManager = useWorkflowManager(workflowKey || '');

  // Determine workflow type from registry
  const workflowType = useMemo(() => {
    if (!workflowKey) return PaymentWorkflowType.Undefined;
    return getWorkflowTypeFromDisplayName(
      workflowsRegistry[workflowKey]?.name || '',
    );
  }, [workflowKey]);

  // Get the appropriate schema based on workflow type
  const getSchemaForWorkflow = useMemo(() => {
    if (!workflowKey) return null;

    // Map workflow keys to schema keys
    const schemaKeyMap: Record<string, keyof typeof schemas> = {
      'dubai-payment-from-office-acc':
        'dubai_payment_from_office_account_payment_details',
      'dubai-transfer-requisition':
        'dubai_transfer_requisition_payment_details',
      'dubai-payment-approval': 'payment_details',
      'overtime-approval': 'overtime_approval_request_details',
      'gift-hospitality-donation-uk':
        'gift_hospitality_donation_uk_request_details',
      'gift-hospitality-donation-dubai':
        'gift_hospitality_donation_dubai_request_details',
      'gift-hospitality-donation-dublin':
        'gift_hospitality_donation_dublin_request_details',
      // PLOP_INJECT_CENTRALISED_SCHEMA_KEY
    };

    const schemaKey = schemaKeyMap[workflowKey];
    if (!schemaKey || !schemas[schemaKey]) {
      logger.warning('No schema found for workflow', { workflowKey });
      return null;
    }

    return schemas[schemaKey];
  }, [workflowKey, schemas]);

  // Fetch workflow data when in edit mode (stop if not allowed)
  useEffect(() => {
    let cancelled = false;

    const fetchWorkflowData = async () => {
      if (!id) return;

      setLoading(true);
      setCreatorFetchSettled(false);
      setError(null);
      resetForm();

      // Testing: registry `permissions: true` — no getWorkFlow / involvement APIs.
      if (shouldBypassWorkflowBackend(workflowType)) {
        const registryEntry = workflowKey
          ? workflowsRegistry[workflowKey]
          : undefined;
        const dummyId =
          id && isTestingDummyWorkflowId(id)
            ? id
            : workflowKey
              ? getTestingDummyWorkflowId(workflowKey)
              : getTestingDummyWorkflowId('workflow');
        const dummyState = buildTestingDummyFormState(workflowType, {
          id: dummyId,
          name: registryEntry?.name || 'Local test request',
        });
        updateFormData(dummyState);
        setLoading(false);
        return;
      }

      // TEMPORARY: Overtime Approval backend permissions are not ready yet.
      if (
        workflowType === PaymentWorkflowType.OvertimeApproval &&
        isDummyOvertimeWorkflowId(id)
      ) {
        const dummyState = buildDummyOvertimeFormState();
        updateFormData(dummyState);
        setLoading(false);
        return;
      }

      if (!currentUser && !originalUser) {
        return;
      }

      try {
        const spoofHeader = getSpoofUserHeader(spoofedUser);
        const spoofIdentity = identityFromSpoofedUser(spoofedUser);
        const tempWorkflowType = workflowType;
        // When spoofing, currentUser is the spoofed person — check their access,
        // including involvement, rather than the admin's type permissions.
        const hasTypeAccess = canViewWorkflow(currentUser, tempWorkflowType);

        if (!hasTypeAccess) {
          const workflowIdNumber = Number(id);
          const [involvementResponse, assignedResponse] = await Promise.all([
            Number.isFinite(workflowIdNumber)
              ? involvementPermissions({
                  workflowId: workflowIdNumber,
                  getAccessToken,
                  spoofedUser: spoofHeader,
                })
              : Promise.resolve({ success: false, data: undefined }),
            getAssignedWorkflows(getAccessToken, spoofHeader),
          ]);

          const assignedWorkflows = assignedResponse.success
            ? (assignedResponse.data?.workflows ?? [])
            : [];

          const hasInvolvementAccess = canAccessWorkflow(
            currentUser,
            tempWorkflowType,
            involvementResponse.success ? involvementResponse.data : undefined,
            assignedWorkflows,
            id,
            spoofIdentity,
          );

          if (!hasInvolvementAccess) {
            logger.warning('User cannot view workflow and is not involved', {
              workflowId: id,
              workflowType: tempWorkflowType,
            });
            navigate('/403');
            return;
          }
        }

        const loadWorkflow = async () => {
          const response = await getWorkFlow({
            id,
            type: workflowType,
            getAccessToken,
            spoofedUser: spoofHeader,
          });

          if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to fetch workflow data');
          }

          return Array.isArray(response.data) ? response.data[0] : response.data;
        };

        const waitForCreator = isGiftHospitalityWorkflow(workflowType);
        let workflowData = await loadWorkflow();

        // createdBy can be missing on the first read after a request is created.
        for (
          let attempt = 1;
          waitForCreator &&
          !hasWorkflowCreator(workflowData.createdBy) &&
          attempt < CREATOR_LOAD_ATTEMPTS;
          attempt += 1
        ) {
          if (cancelled) return;
          await wait(CREATOR_LOAD_DELAY_MS);
          if (cancelled) return;
          workflowData = await loadWorkflow();
        }

        if (cancelled) return;

        // Add workflow type if not present
        if (
          !workflowData.workflowType &&
          workflowType !== PaymentWorkflowType.Undefined
        ) {
          workflowData.workflowType = workflowType;
        }
        const formattedData = mapWorkflowToFormData(workflowData);
        const sectionKeys = extractSectionKeys(formattedData);

        logger.debug('Extracted section keys', { sectionKeys });
        logger.debug('About to update form data', {
          formattedData,
          dataKeys: Object.keys(formattedData.data || {}),
          dataSample: Object.fromEntries(
            Object.entries(formattedData.data || {}).slice(0, 10),
          ),
        });

        // Update the form state with the complete mapped data
        // We need to update both the data and other form state properties
        updateFormData({
          data: formattedData.data,
          currentSection: formattedData.currentSection,
          sectionProgress: formattedData.sectionProgress,
          sectionValidation: formattedData.sectionValidation,
          completedSections: formattedData.completedSections,
          isComplete: formattedData.isComplete,
          isRejected: formattedData.isRejected,
          workflowType: formattedData.workflowType,
          office: formattedData.office, // Add office to the update
          isLoading: false, // Set loading to false after data is loaded
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An unknown error occurred';
        setError(errorMessage);
        logger.error('Error fetching workflow data', { error: err });
      } finally {
        if (!cancelled) {
          setCreatorFetchSettled(true);
          setLoading(false);
        }
      }
    };

    fetchWorkflowData();

    return () => {
      cancelled = true;
    };
  }, [
    id,
    workflowType,
    getAccessToken,
    updateFormData,
    resetForm,
    spoofedUser?.value,
    currentUser,
    originalUser,
    spoofedUser,
    workflowKey,
    navigate,
  ]);

  // Get workflow configuration
  const workflow = useMemo(() => {
    if (!workflowKey) return null;

    try {
      return id
        ? createWorkflowWithAuth(id, workflowKey)
        : workflowsRegistry[workflowKey] || null;
    } catch (error) {
      logger.error('Error creating workflow with auth', { error });
      return workflowsRegistry[workflowKey] || null;
    }
  }, [workflowKey, id]);

  // Create form configuration for single-section workflows
  const bypassBackend = shouldBypassWorkflowBackend(workflowType);
  const singleSectionFormConfig = useMemo(
    () =>
      createSingleSectionFormConfig(
        getSchemaForWorkflow as FormStackProps['formSchema'] | null,
        formConfiguration,
        bypassBackend,
      ),
    [getSchemaForWorkflow, formConfiguration, bypassBackend],
  );

  // Enhanced form submission handler for single-section forms
  const handleSingleSectionSubmit = async (data: Record<string, unknown>) => {
    logger.debug('Single-section form submitted', { data });

    try {
      // Use workflow manager for validation
      const validationResult = workflowManager.validateWorkflow(
        data as FormDataType,
      );

      if (!validationResult.isValid) {
        logger.warning('Form validation failed', {
          errors: validationResult.errors,
        });
        if (workflowManager.workflow?.errorMessages?.validationError) {
          logger.error('Validation error message', {
            message: workflowManager.workflow.errorMessages.validationError,
          });
        }
        return;
      }

      if (formConfiguration.formManager) {
        const result = await formConfiguration.formManager.handleSubmit(
          DEFAULT_SECTION_KEY,
          data,
        );

        if (result.isValid) {
          logger.debug('Form submission successful, triggering completion');
          // Trigger form completion through the ref
          if (formWrapperRef.current) {
            await formWrapperRef.current.handleFormCompletion();
          }
        } else {
          logger.warning('Form submission failed validation', {
            errors: result.errors,
          });
          // Show validation errors to user
          if (workflowManager.workflow?.errorMessages?.validationError) {
            logger.error('Validation error message', {
              message: workflowManager.workflow.errorMessages.validationError,
            });
          }
        }
      } else {
        throw new Error('Form manager not available');
      }
    } catch (error) {
      logger.error('Form submission error', { error });
      // Handle different types of errors
      if (error instanceof Error) {
        if (error.message.includes('network')) {
          logger.error('Network error', {
            message:
              workflowManager.workflow?.errorMessages?.networkError ||
              'Network error',
          });
        } else if (error.message.includes('permission')) {
          logger.error('Permission error', {
            message:
              workflowManager.workflow?.errorMessages?.permissionError ||
              'Permission error',
          });
        } else {
          logger.error('Generic error', {
            message:
              workflowManager.workflow?.errorMessages?.genericError ||
              'Generic error',
          });
        }
      }
    }
  };

  // Enhanced form change handler with auto-save and progress tracking
  const handleSingleSectionChange = useCallback(
    async (data: Record<string, unknown>) => {
      logger.debug('Single-section form data changed', { data });
      formConfiguration.formManager?.updateFormData(data);

      // Auto-save if enabled
      if (workflowManager.workflow?.enableAutoSave) {
        try {
          await workflowManager.autoSave(data as FormDataType);
          logger.debug('Auto-save completed');
        } catch (error) {
          logger.error('Auto-save failed', { error });
        }
      }

      // Calculate and log progress if enabled
      if (workflowManager.workflow?.enableProgressTracking) {
        const progress = workflowManager.calculateProgress(
          data as FormDataType,
        );
        logger.debug('Form progress', { progress: `${progress}%` });
      }
    },
    [formConfiguration.formManager, workflowManager],
  );

  // Form error handler for single-section forms
  const handleSingleSectionError = (errors: Record<string, string>) => {
    logger.debug('Single-section form errors', { errors });
    formConfiguration.formManager?.setErrors(errors);
  };

  const needsCreator =
    isGiftHospitalityWorkflow(workflowType) &&
    Boolean(id) &&
    !shouldBypassWorkflowBackend(workflowType);
  const creatorReady =
    !needsCreator ||
    hasWorkflowCreator(formState.data?.createdBy) ||
    creatorFetchSettled;

  if (loading || !creatorReady) {
    return (
      <Container>
        <Spacer />
        <Heading level={2}>Loading...</Heading>
        <p>Please wait while we load the workflow data.</p>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container>
        <Spacer />
        <Heading level={2}>Error</Heading>
        <p>Failed to load workflow: {error}</p>
      </Container>
    );
  }

  // Workflow not found
  if (!workflow) {
    return (
      <Container>
        <Spacer />
        <Heading level={2}>Workflow not found</Heading>
        <p>The workflow "{workflowKey}" could not be found.</p>
      </Container>
    );
  }

  return (
    <>
      {(bypassBackend || shouldBypassWorkflowBackendByKey(workflowKey)) && (
        <PermissionOverwriteBanner />
      )}
      {workflow.isMultiSection ? (
        <FormStackComponent />
      ) : (
        <FormWrapper
          ref={formWrapperRef}
          showProgress={false}
          showHeader={true}
          isSingleSection={true}
          sectionKey={DEFAULT_SECTION_KEY}
        >
          <FormSection
            sectionKey={DEFAULT_SECTION_KEY}
            formConfig={singleSectionFormConfig}
            onSubmit={handleSingleSectionSubmit}
            onChange={handleSingleSectionChange}
            onError={handleSingleSectionError}
          />
        </FormWrapper>
      )}
      <Spacer size={'1rem'} />
    </>
  );
};
